import type { ColDef, ICellRendererParams } from '../../../components/ui/table';
import CountryFlag from '../../../components/shared/CountryFlag';
import { AUDIT_EXPORT_COLUMNS, formatAuditDateTime } from '../../../lib/auditColumnDefs';
import {
  collectUniqueScalarValues,
  collectUniqueStringValues,
  type GridColumnFilterOption,
} from '../../../components/shared/GridColumnFilterPanel';
import {
  ChipListCell,
  ChipSummaryCell,
  CodeChipCellRenderer,
  GRID_CHIP_TONE,
  GridCellWrap,
  NameAvatarCell,
  PAYMENT_METHOD_CHIP_TONE,
  StatusBadgeCellRenderer,
  SubtitleCellRenderer,
} from '../../../components/shared/agGridCells';
import type { ExcelColumn } from '../../../utils/excelExport';
import { ActionCell } from '../components/ActionCell';
import { ACQUIRER_PARTNER_TYPE } from '../constants/constants';
import type {
  AcquirerColumnFilterKey,
  AcquirerGridContext,
  AcquirerGridRow,
  AcquirerSupportedCurrency,
} from '../models/types';
type AcquirerFilterColumnConfig = {
  key: AcquirerColumnFilterKey;
  label: string;
  type?: 'select' | 'dateRange';
  getOptions?: (rows: Record<string, unknown>[]) => GridColumnFilterOption[];
};

export const ACQUIRER_FILTER_COLUMNS: AcquirerFilterColumnConfig[] = [
  {
    key: 'name',
    label: 'Acquirer',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.name || 'Unknown')),
  },
  {
    key: 'industryType',
    label: 'Industry Type',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.industryType || ACQUIRER_PARTNER_TYPE)),
  },
  {
    key: 'code',
    label: 'Code',
    getOptions:(rows)=> collectUniqueScalarValues(rows, (row) => String(row.code || '—')),
  },
  {
    key: 'country',
    label: 'Country',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.country || '—')),
  },
  {
    key: 'paymentMethods',
    label: 'Payment Methods',
    getOptions: (rows) =>
      collectUniqueStringValues(rows, 'supportedPaymentMethods'),
  },
  {
    key: 'status',
    label: 'Status',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.status || 'Inactive')),
  },
  {
    key: 'trafficTypes',
    label: 'Traffic Type',
    getOptions: (rows) => collectUniqueStringValues(rows, 'trafficTypes'),
  },
  {
    key: 'currencies',
    label: 'Currencies',
    getOptions: (rows) => collectAcquirerCurrencyFilterOptions(rows),
  },
  {
    key: 'totalTerminals',
    label: 'Terminals',
    getOptions: (rows) =>
      collectUniqueScalarValues(rows, (row) => String(row.totalTerminals ?? 0)),
  },
  {
    key: 'terminalNames',
    label: 'Terminal Names',
    getOptions: (rows) => collectUniqueStringValues(rows, 'terminalNames'),
  },
  {
    key: 'createdAt',
    label: 'Created At',
    type: 'dateRange',
  },
];

export function collectAcquirerCurrencyFilterOptions(
  rows: Array<Record<string, unknown>>,
  currencyById?: Record<number, string>,
): GridColumnFilterOption[] {
  const currencies = new Set<string>();

  rows.forEach((row) => {
    const entries = Array.isArray(row.supportedCurrencyEntries)
      ? (row.supportedCurrencyEntries as AcquirerSupportedCurrency[])
      : [];

    entries.forEach((entry) => {
      if (!entry) return;
      const label = (
        entry.currency ||
        (entry.countryId > 0 ? currencyById?.[entry.countryId] : '') ||
        ''
      )
        .trim()
        .toUpperCase();
      if (label) currencies.add(label);
    });

    const codes = row.supportedCurrencies;
    if (Array.isArray(codes)) {
      codes.forEach((code) => {
        if (typeof code === 'string' && code.trim()) {
          currencies.add(code.trim().toUpperCase());
        }
      });
    }
  });

  return Array.from(currencies)
    .sort((a, b) => a.localeCompare(b))
    .map((currency) => ({ value: currency, label: currency }));
}

export const ACQUIRER_COLUMN_LABELS = Object.fromEntries(
  ACQUIRER_FILTER_COLUMNS.map(({ key, label }) => [key, label]),
) as Record<AcquirerColumnFilterKey, string>;

function columnLabel(key: AcquirerColumnFilterKey) {
  return ACQUIRER_COLUMN_LABELS[key];
}

/** Same columns and visual order as the acquirers grid (Action excluded). Status is pinned right. */
export const ACQUIRER_EXPORT_COLUMNS: ExcelColumn[] = [
  { header: columnLabel('name'), key: 'name' },
  { header: columnLabel('industryType'), key: 'industryType' },
  { header: columnLabel('code'), key: 'code' },
  { header: columnLabel('country'), key: 'country' },
  { header: columnLabel('paymentMethods'), key: 'paymentMethods' },
  { header: columnLabel('trafficTypes'), key: 'trafficTypes' },
  { header: columnLabel('currencies'), key: 'currencies' },
  { header: columnLabel('totalTerminals'), key: 'totalTerminals' },
  { header: columnLabel('terminalNames'), key: 'terminalNames' },
  ...AUDIT_EXPORT_COLUMNS,
  { header: columnLabel('status'), key: 'status' },
];

export function buildAcquirerColumnFilterFields(sourceRows: Record<string, unknown>[]) {
  return ACQUIRER_FILTER_COLUMNS.map(({ key, label, getOptions, type }) => ({
    key,
    label,
    type: type ?? 'select',
    options: type === 'dateRange' ? [] : (getOptions?.(sourceRows) ?? []),
  }));
}

function PaymentMethodsCellRenderer(params: ICellRendererParams<AcquirerGridRow>) {
  const ctx = params.context as AcquirerGridContext;
  const methods = params.data?.supportedPaymentMethods || [];

  return (
    <ChipSummaryCell
      items={methods}
      title="Payment Methods"
      tone={GRID_CHIP_TONE.neutral}
      toneForItem={(item) =>
        PAYMENT_METHOD_CHIP_TONE[item.toLowerCase()] ?? GRID_CHIP_TONE.neutral
      }
      onShowMore={ctx.onShowMore}
      onFilterClick={(value) => ctx.onColumnFilter('paymentMethods', value)}
      activeFilter={ctx.columnFilters.paymentMethods}
    />
  );
}

function TrafficTypesCellRenderer(params: ICellRendererParams<AcquirerGridRow>) {
  const ctx = params.context as AcquirerGridContext;

  return (
    <ChipListCell
      items={params.data?.trafficTypes || []}
      title="Traffic Types"
      tone={GRID_CHIP_TONE.category}
      onShowMore={ctx.onShowMore}
      onFilterClick={(value) => ctx.onColumnFilter('trafficTypes', value)}
      activeFilter={ctx.columnFilters.trafficTypes}
    />
  );
}

function CurrenciesCellRenderer(params: ICellRendererParams<AcquirerGridRow>) {
  const ctx = params.context as AcquirerGridContext;
  const entries = params.data?.supportedCurrencyEntries || [];
  const codes = [
    ...new Set(
      entries
        .map((entry) => entry.currency || ctx.currencyById[entry.countryId] || '')
        .map((code) => code.trim().toUpperCase())
        .filter(Boolean),
    ),
  ];
  if (codes.length === 0) {
    codes.push(
      ...(params.data?.supportedCurrencies || [])
        .map((code) => code.trim().toUpperCase())
        .filter(Boolean),
    );
  }

  return (
    <ChipSummaryCell
      items={codes}
      title="Supported Currencies"
      isCurrency
      onShowMore={ctx.onShowMore}
      onFilterClick={(currency) => {
        const normalized = currency.trim().toUpperCase();
        if (normalized) {
          ctx.onColumnFilter('currencies', normalized);
        }
      }}
      activeFilter={ctx.columnFilters.currencies}
    />
  );
}

function TerminalNamesCellRenderer(params: ICellRendererParams<AcquirerGridRow>) {
  const ctx = params.context as AcquirerGridContext;

  return (
    <ChipSummaryCell
      items={params.data?.terminalNames || []}
      title="Terminal Names"
      tone={GRID_CHIP_TONE.terminal}
      onShowMore={ctx.onShowMore}
      onFilterClick={(value) => ctx.onColumnFilter('terminalNames', value)}
      activeFilter={ctx.columnFilters.terminalNames}
    />
  );
}

function joinFilterValues(values?: string[]) {
  return (values ?? []).filter(Boolean).join(' ');
}

export function createAcquirerColumnDefs(): ColDef<AcquirerGridRow>[] {
  return [
    {
      field: 'name',
      headerName: columnLabel('name'),
      flex: 2,
      minWidth: 220,
      cellRenderer: (params: ICellRendererParams<AcquirerGridRow>) => {
        if (!params.data) return null;
        return (
          <NameAvatarCell
            name={params.data.name}
            accent={params.data.accent}
            muted
          />
        );
      },
      sortable: true,
    },
    {
      field: 'industryType',
      headerName: columnLabel('industryType'),
      flex: 1,
      minWidth: 140,
      cellRenderer: SubtitleCellRenderer,
      sortable: true,
    },
    {
      field: 'code',
      headerName: columnLabel('code'),
      width: 120,
      cellRenderer: CodeChipCellRenderer,
      sortable: true,
    },
    {
      field: 'country',
      headerName: columnLabel('country'),
      flex: 1,
      minWidth: 180,
      cellRenderer: (params: ICellRendererParams<AcquirerGridRow>) => (
        <GridCellWrap>
          <span className="inline-flex items-center gap-2">
            <CountryFlag countryName={params.value} />
            <span className="text-muted-foreground">{params.value || '—'}</span>
          </span>
        </GridCellWrap>
      ),
      sortable: true,
    },
    {
      colId: 'paymentMethods',
      headerName: columnLabel('paymentMethods'),
      flex: 1.5,
      minWidth: 210,
      sortable: false,
      filterValueGetter: (params) =>
        joinFilterValues(params.data?.supportedPaymentMethods),
      cellRenderer: PaymentMethodsCellRenderer,
    },
    {
      field: 'status',
      headerName: columnLabel('status'),
      width: 108,
      cellRenderer: StatusBadgeCellRenderer,
      sortable: true,
      pinned:'right'
    },
    {
      colId: 'trafficTypes',
      headerName: columnLabel('trafficTypes'),
      flex: 1,
      minWidth: 180,
      sortable: false,
      filterValueGetter: (params) => joinFilterValues(params.data?.trafficTypes),
      cellRenderer: TrafficTypesCellRenderer,
    },
    {
      colId: 'currencies',
      headerName: columnLabel('currencies'),
      flex: 1,
      minWidth: 180,
      sortable: false,
      filterValueGetter: (params) =>
        joinFilterValues(params.data?.supportedCurrencies),
      cellRenderer: CurrenciesCellRenderer,
    },
    {
      field: 'totalTerminals',
      headerName: columnLabel('totalTerminals'),
      width: 180,
      filterValueGetter: (params) => String(params.data?.totalTerminals ?? 0),
      cellRenderer: (params: ICellRendererParams<AcquirerGridRow>) => {
        const value = Number(params.value);
        const label = value > 0 ? value.toLocaleString() : '—';
        return (
          <GridCellWrap>
            <span className="text-muted-foreground">{label}</span>
          </GridCellWrap>
        );
      },
      sortable: true,
    },
    {
      colId: 'terminalNames',
      headerName: columnLabel('terminalNames'),
      flex: 1,
      minWidth: 180,
      sortable: false,
      filterValueGetter: (params) => joinFilterValues(params.data?.terminalNames),
      cellRenderer: TerminalNamesCellRenderer,
    },
    {
      colId: 'createdAt',
      headerName: columnLabel('createdAt'),
      width: 180,
      cellClass: 'text-muted-foreground',
      valueGetter: (params) => params.data?.createdAt ?? null,
      valueFormatter: (params) => formatAuditDateTime(String(params.value ?? '')),
      filter: {
        component: 'agDateColumnFilter',
        doesFilterPass: () => true,
      },
      filterParams: {
        filterOptions: ['equals', 'inRange'],
        maxNumConditions: 1,
        buttons: ['clear', 'apply'],
        closeOnApply: true,
      },
      sortable: true,
    },
    {
      colId: 'updatedAt',
      headerName: 'Updated At',
      width: 180,
      cellClass: 'text-muted-foreground',
      sortable: true,
      filter: false,
      valueGetter: (params) => params.data?.updatedAt ?? null,
      valueFormatter: (params) => formatAuditDateTime(String(params.value ?? '')),
    },
    {
      headerName: 'Action',
      width: 90,
      minWidth: 90,
      maxWidth: 90,
      sortable: false,
      filter: false,
      cellRenderer: ActionCell,
      pinned: 'right',
    },
  ];
}
